import React, { useState } from 'react';
import { Smartphone, Tablet, Monitor, X, RotateCw } from 'lucide-react';

const DEVICES = [
  { id: 'iphone-se', label: 'iPhone SE', width: 375, height: 667, icon: Smartphone },
  { id: 'iphone-14', label: 'iPhone 14', width: 390, height: 844, icon: Smartphone },
  { id: 'ipad', label: 'iPad', width: 768, height: 1024, icon: Tablet },
  { id: 'desktop', label: 'Desktop', width: 1280, height: 800, icon: Monitor },
];

const PAGES = [
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'Conversations', path: '/conversations' },
  { label: 'NFT Marketplace', path: '/nft-marketplace' },
  { label: 'Hearts Store', path: '/hearts' },
  { label: 'Quests', path: '/quests' },
  { label: 'Settings', path: '/settings' },
  { label: 'Login', path: '/login' },
  { label: 'Homepage', path: '/' },
];

const ResponsivePreview = ({ isOpen, onClose }) => {
  const [device, setDevice] = useState(DEVICES[0]);
  const [page, setPage] = useState(PAGES[0]);
  const [rotated, setRotated] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  if (!isOpen) return null;

  const frameW = rotated ? device.height : device.width;
  const frameH = rotated ? device.width : device.height;
  const baseUrl = window.location.origin;

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex flex-col" data-testid="responsive-preview">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-900 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-white">Preview Mode</span>
          <span className="text-xs text-gray-400 hidden sm:inline">{frameW} x {frameH}px</span>
        </div>

        <div className="flex items-center gap-1.5">
          {/* Device selector */}
          {DEVICES.map(d => {
            const Icon = d.icon;
            return (
              <button
                key={d.id}
                onClick={() => { setDevice(d); setRotated(false); }}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  device.id === d.id ? 'bg-orange-500 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
                data-testid={`preview-device-${d.id}`}
              >
                <Icon size={14} />
                <span className="hidden sm:inline">{d.label}</span>
              </button>
            );
          })}

          <div className="w-px h-5 bg-gray-600 mx-1" />

          {/* Rotate */}
          <button
            onClick={() => setRotated(!rotated)}
            className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
            title="Rotate"
          >
            <RotateCw size={14} />
          </button>

          {/* Page selector */}
          <select
            value={page.path}
            onChange={(e) => {
              const p = PAGES.find(p => p.path === e.target.value);
              if (p) setPage(p);
            }}
            className="bg-gray-800 text-white text-xs rounded-lg px-2 py-1.5 border border-gray-600 outline-none"
            data-testid="preview-page-select"
          >
            {PAGES.map(p => (
              <option key={p.path} value={p.path}>{p.label}</option>
            ))}
          </select>

          {/* Refresh */}
          <button
            onClick={() => setRefreshKey(k => k + 1)}
            className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
            title="Refresh"
          >
            <RotateCw size={14} />
          </button>

          <div className="w-px h-5 bg-gray-600 mx-1" />

          {/* Close */}
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-gray-700 transition-colors"
            data-testid="preview-close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Preview area */}
      <div className="flex-1 flex items-center justify-center p-4 overflow-auto">
        <div
          className="relative bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300"
          style={{
            width: `${Math.min(frameW, window.innerWidth - 48)}px`,
            height: `${Math.min(frameH, window.innerHeight - 100)}px`,
            border: '8px solid #1A1209',
            borderRadius: device.id.includes('iphone') ? '28px' : device.id === 'ipad' ? '16px' : '8px',
          }}
        >
          {/* Notch for phones */}
          {device.id.includes('iphone') && !rotated && (
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-gray-900 rounded-b-xl z-10" />
          )}
          <iframe
            key={`${page.path}-${device.id}-${rotated}-${refreshKey}`}
            src={`${baseUrl}${page.path}`}
            title="Preview"
            className="w-full h-full border-0"
            style={{ transformOrigin: 'top left' }}
          />
        </div>
      </div>

      {/* Device label */}
      <div className="text-center pb-3">
        <span className="text-xs text-gray-500">{device.label} &mdash; {page.label} &mdash; {frameW}x{frameH}</span>
      </div>
    </div>
  );
};

export default ResponsivePreview;
